/**
 * Self-test suite: Call display + history + DND policies (CallKit-equivalent)
 *
 * Mirrors iOS `CallKitSelfTest.swift` exactly. Three groups correspond to the
 * three policy modules:
 *
 *   Group A — CallDisplayPolicy (9 cases)
 *     A1. callUiEnabled=false → undefined decision (in-app-only fallback)
 *     A2. in-roster + trusted name → displayName == trusted name
 *     A3. in-roster without name → 8-char userId prefix + ellipsis
 *     A4. not-in-roster → displayName == "Unknown caller"
 *     A5. in-roster with empty-string name → 8-char userId prefix (no empty display)
 *     A6. handleValue is always the userId, never a name
 *     A7. peer-claimed name field is structurally absent (CallerProfile invariant)
 *     A8. recordInHistory inverts suppressFromHistory
 *     A9. bypassDND tracks allowDNDOverride
 *
 *   Group B — CallHistoryPolicy (4 cases)
 *     B1. markCallEnded is true for every outcome
 *     B2. removeFromHistoryAfterEnd follows suppressFromHistory (all outcomes)
 *     B3. preserveCallDurationInApp is true only for answeredLocal/answeredRemote
 *     B4. preserveCallDurationInApp is false for missed/declined/failed
 *
 *   Group C — CallDNDPolicy (5 cases)
 *     C1. allowDNDOverride=true → bypass with "explicit peer-override permission"
 *     C2. no override + no repeat → DND respected with "default DND respected"
 *     C3. repeat ≤ 180 s + no per-peer override → bypass with "repeat call within 3 min"
 *     C4. repeat > 180 s → DND respected (window expired, resets the counter)
 *     C5. repeat exactly 180 s (boundary) → bypass (inclusive threshold)
 *
 * All cases are pure; nothing touches the Notification API, IndexedDB, or any
 * browser API. Safe to run on every dev launch.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import type { CallerProfile, PeerCallSettings } from "./CallDisplayPolicy";
import { decideCallDisplay } from "./CallDisplayPolicy";
import type { CallOutcome } from "./CallHistoryPolicy";
import { decideForCallEnded } from "./CallHistoryPolicy";
import { decideCallDND } from "./CallDNDPolicy";

// ---------------------------------------------------------------------------
// Fixtures
// ---------------------------------------------------------------------------

const LONG_USER_ID = "01HZZZZZZZZZZZZZZZZZZZZZZA"; // 26-char ULID-shaped id

const rosterCallerWithName: CallerProfile = {
  userId: LONG_USER_ID,
  trustedDisplayName: "Alice",
  isInRoster: true,
};

const rosterCallerWithoutName: CallerProfile = {
  userId: LONG_USER_ID,
  trustedDisplayName: undefined,
  isInRoster: true,
};

const rosterCallerWithEmptyName: CallerProfile = {
  userId: LONG_USER_ID,
  trustedDisplayName: "",
  isInRoster: true,
};

const strangerCaller: CallerProfile = {
  userId: LONG_USER_ID,
  trustedDisplayName: undefined,
  isInRoster: false,
};

const defaultSettings: PeerCallSettings = {
  suppressFromHistory: false,
  showCallerName: true,
  allowDNDOverride: false,
};

// ---------------------------------------------------------------------------
// Group A — CallDisplayPolicy
// ---------------------------------------------------------------------------

function testA1_callUiDisabledReturnsUndefined(): TestCase {
  const d = decideCallDisplay(rosterCallerWithName, defaultSettings, false);
  const ok = d === undefined;
  return ok
    ? pass("A1: callUiEnabled=false returns undefined")
    : fail("A1: callUiEnabled=false returns undefined", `got ${JSON.stringify(d)}`);
}

function testA2_inRosterWithTrustedName(): TestCase {
  const d = decideCallDisplay(rosterCallerWithName, defaultSettings, true);
  const ok = d?.displayName === "Alice";
  return ok
    ? pass("A2: in-roster + trusted name uses trusted name")
    : fail("A2: in-roster + trusted name uses trusted name", `got ${JSON.stringify(d?.displayName)}`);
}

function testA3_inRosterWithoutName(): TestCase {
  const d = decideCallDisplay(rosterCallerWithoutName, defaultSettings, true);
  const expected = LONG_USER_ID.slice(0, 8) + "…";
  const ok = d?.displayName === expected;
  return ok
    ? pass("A3: in-roster without name uses 8-char userId prefix")
    : fail("A3: in-roster without name uses 8-char userId prefix", `got ${JSON.stringify(d?.displayName)}, want ${expected}`);
}

function testA4_notInRoster(): TestCase {
  const d = decideCallDisplay(strangerCaller, defaultSettings, true);
  const ok = d?.displayName === "Unknown caller";
  return ok
    ? pass("A4: not-in-roster → \"Unknown caller\"")
    : fail("A4: not-in-roster → \"Unknown caller\"", `got ${JSON.stringify(d?.displayName)}`);
}

function testA5_emptyNameFallsBackToPrefix(): TestCase {
  const d = decideCallDisplay(rosterCallerWithEmptyName, defaultSettings, true);
  const expected = LONG_USER_ID.slice(0, 8) + "…";
  const ok = d?.displayName === expected;
  return ok
    ? pass("A5: in-roster with empty name falls back to userId prefix")
    : fail("A5: in-roster with empty name falls back to userId prefix", `got ${JSON.stringify(d?.displayName)}, want ${expected}`);
}

function testA6_handleValueIsUserId(): TestCase {
  const d1 = decideCallDisplay(rosterCallerWithName, defaultSettings, true);
  const d2 = decideCallDisplay(strangerCaller, defaultSettings, true);
  const ok =
    d1?.handleValue === rosterCallerWithName.userId &&
    d2?.handleValue === strangerCaller.userId;
  return ok
    ? pass("A6: handleValue is always the userId, never a name")
    : fail("A6: handleValue is always the userId, never a name", `d1=${JSON.stringify(d1?.handleValue)} d2=${JSON.stringify(d2?.handleValue)}`);
}

function testA7_noClaimedDisplayNameFieldInvariant(): TestCase {
  // Structural invariant: CallerProfile must not carry a `claimedDisplayName`
  // field. We assert this by constructing a CallerProfile and verifying that
  // the field is absent from its own keys. If a future diff adds such a field
  // this case fails at runtime, surfacing the change for review.
  const profile: CallerProfile = {
    userId: "u-1",
    isInRoster: false,
  };
  const hasClaimField = "claimedDisplayName" in profile;
  const da = decideCallDisplay(profile, defaultSettings, true);
  const ok = !hasClaimField && da?.displayName === "Unknown caller";
  return ok
    ? pass("A7: CallerProfile has no claimedDisplayName field (invariant)")
    : fail("A7: CallerProfile has no claimedDisplayName field (invariant)", `hasClaimField=${hasClaimField} displayName=${JSON.stringify(da?.displayName)}`);
}

function testA8_recordInHistoryInvertsSuppressFromHistory(): TestCase {
  const withSuppressOff = decideCallDisplay(
    rosterCallerWithName,
    { ...defaultSettings, suppressFromHistory: false },
    true,
  );
  const withSuppressOn = decideCallDisplay(
    rosterCallerWithName,
    { ...defaultSettings, suppressFromHistory: true },
    true,
  );
  const ok =
    withSuppressOff?.recordInHistory === true &&
    withSuppressOn?.recordInHistory === false;
  return ok
    ? pass("A8: recordInHistory inverts suppressFromHistory")
    : fail("A8: recordInHistory inverts suppressFromHistory", `off=${withSuppressOff?.recordInHistory} on=${withSuppressOn?.recordInHistory}`);
}

function testA9_bypassDNDTracksAllowDNDOverride(): TestCase {
  const withOverrideOff = decideCallDisplay(
    rosterCallerWithName,
    { ...defaultSettings, allowDNDOverride: false },
    true,
  );
  const withOverrideOn = decideCallDisplay(
    rosterCallerWithName,
    { ...defaultSettings, allowDNDOverride: true },
    true,
  );
  const ok =
    withOverrideOff?.bypassDND === false &&
    withOverrideOn?.bypassDND === true;
  return ok
    ? pass("A9: bypassDND tracks allowDNDOverride")
    : fail("A9: bypassDND tracks allowDNDOverride", `off=${withOverrideOff?.bypassDND} on=${withOverrideOn?.bypassDND}`);
}

// ---------------------------------------------------------------------------
// Group B — CallHistoryPolicy
// ---------------------------------------------------------------------------

const ALL_OUTCOMES: CallOutcome[] = [
  "answeredLocal",
  "answeredRemote",
  "missed",
  "declined",
  "failed",
];

function testB1_markCallEndedAlwaysTrue(): TestCase {
  for (const outcome of ALL_OUTCOMES) {
    const d = decideForCallEnded(defaultSettings, outcome);
    if (!d.markCallEnded) {
      return fail(
        "B1: markCallEnded is true for every outcome",
        `outcome=${outcome} got false`,
      );
    }
  }
  return pass("B1: markCallEnded is true for every outcome");
}

function testB2_removeFromHistoryFollowsSuppressFromHistory(): TestCase {
  const suppressOn: Pick<PeerCallSettings, "suppressFromHistory"> = {
    suppressFromHistory: true,
  };
  const suppressOff: Pick<PeerCallSettings, "suppressFromHistory"> = {
    suppressFromHistory: false,
  };
  for (const outcome of ALL_OUTCOMES) {
    const on = decideForCallEnded(suppressOn, outcome);
    const off = decideForCallEnded(suppressOff, outcome);
    if (!on.removeFromHistoryAfterEnd || off.removeFromHistoryAfterEnd) {
      return fail(
        "B2: removeFromHistoryAfterEnd follows suppressFromHistory",
        `outcome=${outcome} on=${on.removeFromHistoryAfterEnd} off=${off.removeFromHistoryAfterEnd}`,
      );
    }
  }
  return pass("B2: removeFromHistoryAfterEnd follows suppressFromHistory (all outcomes)");
}

function testB3_preserveDurationTrueForAnswered(): TestCase {
  const answered: CallOutcome[] = ["answeredLocal", "answeredRemote"];
  for (const outcome of answered) {
    const d = decideForCallEnded(defaultSettings, outcome);
    if (!d.preserveCallDurationInApp) {
      return fail(
        "B3: preserveCallDurationInApp is true for answered calls",
        `outcome=${outcome} got false`,
      );
    }
  }
  return pass("B3: preserveCallDurationInApp is true for answeredLocal/answeredRemote");
}

function testB4_preserveDurationFalseForNonAnswered(): TestCase {
  const nonAnswered: CallOutcome[] = ["missed", "declined", "failed"];
  for (const outcome of nonAnswered) {
    const d = decideForCallEnded(defaultSettings, outcome);
    if (d.preserveCallDurationInApp) {
      return fail(
        "B4: preserveCallDurationInApp is false for missed/declined/failed",
        `outcome=${outcome} got true`,
      );
    }
  }
  return pass("B4: preserveCallDurationInApp is false for missed/declined/failed");
}

// ---------------------------------------------------------------------------
// Group C — CallDNDPolicy
// ---------------------------------------------------------------------------

function testC1_explicitOverrideBypassesDND(): TestCase {
  const s: PeerCallSettings = { ...defaultSettings, allowDNDOverride: true };
  const d = decideCallDND(s, undefined);
  const ok = d.shouldBypassDND && d.reason === "explicit peer-override permission";
  return ok
    ? pass("C1: allowDNDOverride=true bypasses with explicit-permission reason")
    : fail("C1: allowDNDOverride=true bypasses with explicit-permission reason", `got shouldBypass=${d.shouldBypassDND} reason=${JSON.stringify(d.reason)}`);
}

function testC2_noOverrideNoRepeatRespectsDND(): TestCase {
  const s: PeerCallSettings = { ...defaultSettings, allowDNDOverride: false };
  const d = decideCallDND(s, undefined);
  const ok = !d.shouldBypassDND && d.reason === "default DND respected";
  return ok
    ? pass("C2: no override + no repeat → DND respected")
    : fail("C2: no override + no repeat → DND respected", `got shouldBypass=${d.shouldBypassDND} reason=${JSON.stringify(d.reason)}`);
}

function testC3_repeatWithinWindowBypassesDND(): TestCase {
  const s: PeerCallSettings = { ...defaultSettings, allowDNDOverride: false };
  const d = decideCallDND(s, 60); // 60 s — well within 180 s window
  const ok = d.shouldBypassDND && d.reason === "repeat call within 3 min";
  return ok
    ? pass("C3: repeat call within 180 s bypasses DND")
    : fail("C3: repeat call within 180 s bypasses DND", `got shouldBypass=${d.shouldBypassDND} reason=${JSON.stringify(d.reason)}`);
}

function testC4_repeatOutsideWindowRespectsDND(): TestCase {
  const s: PeerCallSettings = { ...defaultSettings, allowDNDOverride: false };
  const d = decideCallDND(s, 181); // 181 s — just outside window, counter resets
  const ok = !d.shouldBypassDND && d.reason === "default DND respected";
  return ok
    ? pass("C4: repeat call beyond 3-min window → DND respected (counter reset)")
    : fail("C4: repeat call beyond 3-min window → DND respected (counter reset)", `got shouldBypass=${d.shouldBypassDND} reason=${JSON.stringify(d.reason)}`);
}

function testC5_repeatAtExactBoundaryBypassesDND(): TestCase {
  const s: PeerCallSettings = { ...defaultSettings, allowDNDOverride: false };
  const d = decideCallDND(s, 180); // exactly 180 s — inclusive threshold
  const ok = d.shouldBypassDND && d.reason === "repeat call within 3 min";
  return ok
    ? pass("C5: repeat call at exactly 180 s (boundary) bypasses DND (inclusive)")
    : fail("C5: repeat call at exactly 180 s (boundary) bypasses DND (inclusive)", `got shouldBypass=${d.shouldBypassDND} reason=${JSON.stringify(d.reason)}`);
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Call display + history + DND policies (CallKit-equivalent)",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      // Group A — CallDisplayPolicy
      testA1_callUiDisabledReturnsUndefined(),
      testA2_inRosterWithTrustedName(),
      testA3_inRosterWithoutName(),
      testA4_notInRoster(),
      testA5_emptyNameFallsBackToPrefix(),
      testA6_handleValueIsUserId(),
      testA7_noClaimedDisplayNameFieldInvariant(),
      testA8_recordInHistoryInvertsSuppressFromHistory(),
      testA9_bypassDNDTracksAllowDNDOverride(),

      // Group B — CallHistoryPolicy
      testB1_markCallEndedAlwaysTrue(),
      testB2_removeFromHistoryFollowsSuppressFromHistory(),
      testB3_preserveDurationTrueForAnswered(),
      testB4_preserveDurationFalseForNonAnswered(),

      // Group C — CallDNDPolicy
      testC1_explicitOverrideBypassesDND(),
      testC2_noOverrideNoRepeatRespectsDND(),
      testC3_repeatWithinWindowBypassesDND(),
      testC4_repeatOutsideWindowRespectsDND(),
      testC5_repeatAtExactBoundaryBypassesDND(),
    ]),
});
